import { isRecord, List } from 'immutable';
import type { UnknownAction } from 'redux';

import { resourceStoreTypes, resourceUpserters } from '../core';
import type { QcsSyncResourcesPayload } from '../core/resourceStoreTypes';
import * as actions from './subscriptionsActions';
import {
  SUBSCRIPTIONS_REDUCER_KEY,
  type SubscriptionProps,
  type SubscriptionRecord,
  type SubscriptionsStore,
} from './subscriptionsTypes';

const supportedDatasetPlatformsOverrides = new Map<string, Pick<SubscriptionProps, 'supportedDatasetPlatforms'>>([
  ['acme', { supportedDatasetPlatforms: List(['ACME']) }],
  ['acme-apple', { supportedDatasetPlatforms: List(['ACME']) }],
  ['acme-google', { supportedDatasetPlatforms: List(['ACME']) }],
]);

const mkSubscriptionsStore = resourceStoreTypes.createTypedQcsSyncResourceStoreFactory<SubscriptionProps>();

const initialState = mkSubscriptionsStore();

const applySupportedDatasetPlatformsOverride = (subscription: SubscriptionRecord): SubscriptionRecord => {
  const override = supportedDatasetPlatformsOverrides.get(subscription.productLineUriName ?? '');
  return override ? subscription.merge(override) : subscription;
};

const addSupportedDatasetPlatformsOverrides = (
  payload: QcsSyncResourcesPayload<SubscriptionProps>,
): QcsSyncResourcesPayload<SubscriptionProps> => {
  const resources = payload.resources.map(applySupportedDatasetPlatformsOverride);
  const payloadWithOverrides = {
    resources,
    replaceCollection: true,
  };

  return isRecord(payload) ? payload.merge(payloadWithOverrides) : { ...payload, ...payloadWithOverrides };
};

function subscriptionsReducer(state = initialState, action: UnknownAction): SubscriptionsStore {
  if (actions.getSubscriptions.match(action)) {
    return state.merge({ isLoading: true });
  }

  if (actions.getSubscriptionsSuccess.match(action)) {
    return resourceUpserters.upsertResources<SubscriptionProps>(
      state,
      addSupportedDatasetPlatformsOverrides(action.payload),
    );
  }

  if (actions.getSubscriptionsFailure.match(action)) {
    return resourceUpserters.completeWithError<SubscriptionProps>(state, action.payload);
  }

  return state;
}

export { SUBSCRIPTIONS_REDUCER_KEY };
export default subscriptionsReducer;
